import { BooleanLiteral } from 'typescript';
import { StateStatus } from '../../enums/StateStatus';
import Equipment from '../../types/model/Equipment';
import { RestErrorResponse } from '../../types/response/RestErrorResponse';
import { Barrier, BarrierDeviceMode } from '../barriers/types';
import { UserRole } from '../user/types';
import { InternalUser } from '../users/types';

export enum ProjectStatus {
  Active = 'Active',
  Disabled = 'Disabled',
  Archived = 'Archived'
}

export interface ProjectState {
  projects: Project[] | null;
  projectsCopy: Project[] | null;
  projectDetails: ProjectDetails | null;
  projectDocuments: ProjectDocument[] | null;
  internalUserProjects: Project[] | null;
  externalUserProjects: Project[] | null;
  projectSettings: ProjectSettings | null;
  projectContacts: ContactPerson[] | null;
  internalUsers: InternalUser[] | null;
  status: StateStatus;
  error: RestErrorResponse | null;
}

export interface ProjectDocument {
  id: number;
  name: string;
  createdBy: string;
  createdOn: string;
  profilePhoto: string;
  link: string;
  docType: string;
  fullName: string;
  projectId: number;
  isProjectPlan: boolean;
  uploadDate: string;
  uploadTime: string;
}

export interface Project {
  code: string;
  constructionSite: string;
  constructionArea: string;
  description: string;
  id: number;
  lastBooking: string;
  name: string;
  photo: string;
  city: string;
  addressLine1: string;
  postCode: string;
  roleName: string;
  status: number;
  statusEnum: ProjectStatus;
  hasLinkedBarrierDevice: boolean;
}

export interface ProjectList {
  projects: Project[];
}
export interface ConstructionSite {
  id: number;
  address: string;
  deliveryNotes: string;
}

export interface File {
  id: number;
  name: string;
  createdBy: string;
  createdOn: string;
  fullName: string;
}

export interface ProjectDetails {
  id: number;
  code: string;
  name: string;
  constructionArea: string;
  constructionSite: ConstructionSite;
  projectPlanDocuments: File[];
  image: File;
  projectContacts: ProjectContact[] | null;
  userRole: UserRole[];
}

export interface ProjectContact {
  id: number;
  userAccountID: number;
  firstName: string;
  lastName: string;
  title: string;
  phoneNumber: string;
  mobilePhoneNumber: string;
}

export interface FileContent {
  contentType: string;
  enableRangeProcessing: BooleanLiteral;
  entityTag: string;
  fileContents: string;
  fileDownloadName: string;
  lastModified: string;
}

export interface ContactPerson {
  id: number;
  userAccountID: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  mobilePhoneNumber: string;
  title: string;
}

export interface ContactPersonSearch {
  projectDetailsContactPersonSearch: ContactPerson[];
}

export interface ProjectSettings {
  operatingHoursFrom: string;
  operatingHoursTo: string;
  deliveryDurationMin: number;
  deliveryDurationMax: number;
  automaticConfirmationTime: number;
  bookingDeadline: number;
  isInBarrierSystem: boolean;
  masterPin: string;
  includeSaturdays: boolean;
  includeSundays: boolean;
}

export interface SubcontractorAccount {
  subcontractor: {
    id: number;
    name: string;
    email: string;
    address: string;
    postalCode: string;
    city: string;
    phoneNumber: string;
    notificationsEmail: string;
    notificationsContactName: string;
  };
  primaryContactDetails: PrimaryContact;
}

export interface PrimaryContact {
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface SubcontractorDetailsRequest {
  name: string;
  email: string;
  address: string;
  postalCode: string;
  city: string;
  phoneNumber: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactPhoneNumber: string;
  projectId: number;
  notificationsEmail: string;
  notificationsContactName: string;
}

export interface DeliveryZoneSettings {
  id: number;
  name: string;
  active: true;
  barrierSystemStatus: true;
  barrierDevice: Barrier;
  address: string;
  order: number;
  equipment: Equipment[];
}

export interface ProjectBarrierDevice {
  id: number;
  serialNumber: string;
  deliveryZoneIds: number;
  mode: BarrierDeviceMode;
  inventoryNumber: number;
}

export interface ProjectDeliveryZoneSettings {
  projectDeliveryZones: DeliveryZoneSettings[];
  projectBarrierDevices: ProjectBarrierDevice[];
}

export interface NotificationsEmail {
  notificationsEmail: string;
  projectId: number;
  notificationsContactName: string;
}
